/**
 * Tauri ``AndoStorage`` adapter — backed by ``@tauri-apps/plugin-store``.
 *
 * The plugin is declared as an optional peerDependency so web-only builds
 * don't pay the install cost. We load it lazily via dynamic import to keep
 * import-side-effects clean (tree-shakeable on the web bundle).
 *
 * Falls back to ``WebStorage`` if the plugin isn't installed / Tauri APIs
 * aren't available at runtime — keeps tests + Storybook from crashing.
 */

import type { AndoStorage } from "./types.js";
import { WebStorage } from "./web.js";

interface TauriStore {
  get<T>(key: string): Promise<T | null>;
  set<T>(key: string, value: T): Promise<void>;
  delete(key: string): Promise<boolean>;
  clear(): Promise<void>;
  save(): Promise<void>;
}

type TauriStoreLoader = (path: string) => Promise<TauriStore>;

const STORE_PATH = ".ando-store.dat";

export class TauriStorage implements AndoStorage {
  private storePromise: Promise<TauriStore | null> | null = null;
  private fallback: WebStorage | null = null;

  private async getStore(): Promise<TauriStore | null> {
    if (!this.storePromise) {
      this.storePromise = (async () => {
        try {
          // Dynamic import via runtime variable so the bundler doesn't try to
          // resolve `@tauri-apps/plugin-store` when not in a Tauri context.
          // The plugin type surface evolves across major versions — we cast to
          // a structural shape we can safely consume.
          const moduleId = "@tauri-apps/plugin-store";
          const mod = (await import(/* @vite-ignore */ /* webpackIgnore: true */ moduleId)) as {
            load?: TauriStoreLoader;
            Store?: { load: TauriStoreLoader };
          };
          if (typeof mod.load === "function") {
            return (await mod.load(STORE_PATH)) as TauriStore;
          }
          if (mod.Store && typeof mod.Store.load === "function") {
            return (await mod.Store.load(STORE_PATH)) as TauriStore;
          }
          return null;
        } catch {
          return null;
        }
      })();
    }
    return this.storePromise;
  }

  private getFallback(): WebStorage {
    if (!this.fallback) this.fallback = new WebStorage();
    return this.fallback;
  }

  async get<T>(key: string): Promise<T | null> {
    const store = await this.getStore();
    if (!store) return this.getFallback().get<T>(key);
    return (await store.get<T>(key)) ?? null;
  }

  async set<T>(key: string, value: T): Promise<void> {
    const store = await this.getStore();
    if (!store) return this.getFallback().set<T>(key, value);
    await store.set(key, value);
    await store.save();
  }

  async remove(key: string): Promise<void> {
    const store = await this.getStore();
    if (!store) return this.getFallback().remove(key);
    await store.delete(key);
    await store.save();
  }

  async clear(): Promise<void> {
    const store = await this.getStore();
    if (!store) return this.getFallback().clear();
    await store.clear();
    await store.save();
  }
}
